import React from "react";
import { ImagePlus, X } from "lucide-react";
import { CtaButton } from "@/main/react/buttons/CtaButton";
import { SafeImage } from "@/main/util/SafeImage";
import logger from "@/lib/logger";
import type { Localizer } from "@/main/react/Types";
import { MAX_REPORT_ATTACHMENTS } from "@/main/common/ReportTypes";
import {
    getAcceptedUploadAcceptAttribute,
    getAcceptedUploadFormatLabel,
    getServerErrorMessage,
    getUploadErrorDiagnostics,
    isAcceptedUploadType,
    isLikelyHeicUpload,
    maxUploadSizeBytesForType,
    uploadImageFile,
} from "@/main/react/image/imageUploadService";

export type ReportAttachment = {
    imagePublicId: string;
    thumbnailUrl: string;
    name?: string;
};

type Props = {
    localizer: Localizer;
    attachments: ReportAttachment[];
    onChange: (attachments: ReportAttachment[]) => void;
    disabled?: boolean;
    /** Distinguishes the two dialogs' playwright hooks and input ids. */
    idPrefix: string;
};

/**
 * "Add screenshots" for the report dialogs.
 *
 * <p><b>Not a new uploader.</b> Every gate and the whole presign → S3 → mark-uploaded sequence come from
 * `imageUploadService`, which is the same module the image picker and the editor paste/drop path use.
 * That is what makes the report path inherit the server's content-type allowlist, its 20 MB ceiling and
 * all three of its per-window rate ceilings without restating any of them here: `uploadImageFile` calls
 * the one presign endpoint, and there is no other way to obtain an upload URL.
 *
 * <p>Clips are deliberately not offered (`allowClips: false`). A report is evidence of something that
 * already happened; a 30-second video is a review cost with no matching benefit, and the accept
 * attribute is derived from the shared gate rather than hand-assembled so it cannot drift from what the
 * presign will take.
 *
 * <p>The cap here is a HINT. `ReportAttachmentService` enforces it server-side against what is already
 * stored, which is the only place it can be enforced across two submissions.
 */
export function ReportAttachmentPicker({ localizer, attachments, onChange, disabled, idPrefix }: Props) {
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    const full = attachments.length >= MAX_REPORT_ATTACHMENTS;
    const inputId = `${idPrefix}-attachment-input`;

    const resetInput = () => {
        if (inputRef.current) {
            inputRef.current.value = "";
        }
    };

    const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }
        setError(null);

        // Both gates below return BEFORE uploadImageFile, so neither the server's presign WARN nor the
        // upload step annotation can ever record them. Without these lines a member failing here leaves
        // no trace at all. Log the MIME type and size, never the filename: a filename is user content,
        // and on a harassment report it can itself be sensitive.
        if (!isAcceptedUploadType(file.type, { allowClips: false })) {
            logger.warn("[ReportAttachmentPicker] upload rejected before presign", {
                rejectionReason: "type",
                mimeType: file.type || "unknown",
                sizeBytes: file.size,
                surface: "report-attachment",
            });
            setError(isLikelyHeicUpload(file)
                ? localizer("typescript.image.upload.heic_unsupported")
                : localizer("typescript.image.upload.invalid.format", getAcceptedUploadFormatLabel({ allowClips: false })));
            resetInput();
            return;
        }

        if (file.size > maxUploadSizeBytesForType(file.type)) {
            logger.warn("[ReportAttachmentPicker] upload rejected before presign", {
                rejectionReason: "size",
                mimeType: file.type || "unknown",
                sizeBytes: file.size,
                surface: "report-attachment",
            });
            setError(localizer("typescript.image.upload.size.exceeded"));
            resetInput();
            return;
        }

        setBusy(true);
        try {
            const uploaded = await uploadImageFile(file, { localizer });
            // Re-read the cap against the value at completion rather than the one captured when the
            // picker opened: two uploads can be in flight, and the stale closure would let the second
            // one through.
            onChange([
                ...attachments,
                {
                    imagePublicId: uploaded.imagePublicId,
                    thumbnailUrl: uploaded.thumbnailUrl ?? uploaded.url ?? "",
                    name: uploaded.name,
                },
            ].slice(0, MAX_REPORT_ATTACHMENTS));
        } catch (err: any) {
            logger.error("[ReportAttachmentPicker] upload error", err, getUploadErrorDiagnostics(err));
            setError(getServerErrorMessage(err) ?? err?.message ?? localizer("typescript.image.upload.failed"));
        } finally {
            setBusy(false);
            resetInput();
        }
    };

    return (
        <div className="flex flex-col gap-2" data-playwright-id={`${idPrefix}-attachments`}>
            <label className="text-sm font-medium" htmlFor={inputId}>
                {localizer("report.attachment.label")}
            </label>

            {attachments.length > 0 ? (
                <ul className="flex flex-wrap gap-2" data-playwright-id={`${idPrefix}-attachment-list`}>
                    {attachments.map((attachment) => (
                        <li
                            key={attachment.imagePublicId}
                            className="relative h-20 w-20 overflow-hidden rounded-lg border border-border/70 bg-background"
                            data-playwright-id={`${idPrefix}-attachment-item`}
                            data-report-attachment-id={attachment.imagePublicId}
                        >
                            <SafeImage
                                src={attachment.thumbnailUrl}
                                alt={attachment.name ?? localizer("report.attachment.label")}
                                className="h-full w-full object-cover"
                            />
                            <button
                                type="button"
                                onClick={() => onChange(attachments.filter((a) => a.imagePublicId !== attachment.imagePublicId))}
                                disabled={disabled || busy}
                                aria-label={localizer("report.attachment.remove")}
                                title={localizer("report.attachment.remove")}
                                data-playwright-id={`${idPrefix}-attachment-remove`}
                                className="absolute right-1 top-1 touch-hit-44 inline-flex h-6 w-6 items-center justify-center rounded-full border border-border/70 bg-background/90 text-foreground outline-none transition-colors hover:bg-accent focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                <X className="h-3.5 w-3.5" aria-hidden="true" />
                            </button>
                        </li>
                    ))}
                </ul>
            ) : null}

            <input
                ref={inputRef}
                id={inputId}
                type="file"
                accept={getAcceptedUploadAcceptAttribute({ allowClips: false })}
                style={{ display: "none" }}
                data-playwright-id={`${idPrefix}-attachment-file-input`}
                onChange={handleChange}
            />

            <div>
                <CtaButton
                    type="button"
                    variant="outline"
                    size="sm"
                    icon={ImagePlus}
                    text={busy ? localizer("image.list.uploading") : localizer("report.attachment.add")}
                    onClick={() => inputRef.current?.click()}
                    disabled={disabled || busy || full}
                    extraClass=""
                    playwrightId={`${idPrefix}-attachment-add`}
                />
            </div>

            <p className="text-xs text-muted-foreground">
                {full ? localizer("report.attachment.limit_reached") : localizer("report.attachment.help")}
            </p>

            {error ? (
                <p className="text-xs text-destructive-text" data-playwright-id={`${idPrefix}-attachment-error`}>
                    {error}
                </p>
            ) : null}
        </div>
    );
}

export default ReportAttachmentPicker;
